import { cloneElement } from "react"
import { Box, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { CompareButtonsBelow } from "slot-definitions"
import { findTask } from "routes/schedule/compare/find-task"
import { ScheduleReviewJustification } from "routes/schedule/compare/schedule-review-justification"
import { If } from "lib/@components/switch"
import { AuthoringSideBySideRow } from "./authoring-side-by-side-row"
import { AuthoringReviewIntroValue } from "./authoring-review-intro-value"
import { AuthoringCompareTask } from "./authoring-compare-task"
import { AuthoringCompareMissingTask } from "./authoring-compare-missing-task"

export function AuthoringCompareBothPanel() {
    CompareButtonsBelow.usePlug(<ScheduleReviewJustification />)

    const { compare, comparisonTarget, comparisons } = useBoundContext()
    const missingTasks = comparisonTarget.tasks.filter((task) => !findTask(compare.tasks, task))
    const movements = comparisons?.find((item) => item?.props?.children?.props?.componentType === "Movements") ?? null
    const guidance =
        comparisons?.filter((item) =>
            ["NewGuidance", "RemovedGuidance"].includes(item?.props?.children?.props?.componentType)
        ) ?? []
    const guidanceItems = guidance.map((item, index) => cloneElement(item, { key: item.key ?? index }))

    return (
        <Bound editInDialog={true}>
            <AuthoringSideBySideRow
                dataCy="side-by-side-title-row"
                left={<DetailsFieldValue label="Title" value={compare.title} dataCy="side-by-side-details-title" />}
                right={
                    <DetailsFieldValue
                        label="Title"
                        value={comparisonTarget.title}
                        dataCy="side-by-side-details-title-comparison"
                    />
                }
            />
            <AuthoringSideBySideRow
                dataCy="side-by-side-code-row"
                left={<DetailsFieldValue label="Code" value={compare.code} dataCy="side-by-side-details-code" />}
                right={
                    <DetailsFieldValue
                        label="Code"
                        value={comparisonTarget.code}
                        dataCy="side-by-side-details-code-comparison"
                    />
                }
            />
            <AuthoringSideBySideRow
                dataCy="side-by-side-unit-of-measure-row"
                left={
                    <DetailsFieldValue
                        label="Unit of Measure"
                        value={compare.unitOfMeasure}
                        dataCy="side-by-side-details-unit-of-measure"
                    />
                }
                right={
                    <DetailsFieldValue
                        label="Unit of Measure"
                        value={comparisonTarget.unitOfMeasure}
                        dataCy="side-by-side-details-unit-of-measure-comparison"
                    />
                }
            />
            <AuthoringSideBySideRow
                dataCy="side-by-side-intro-content-row"
                left={
                    <AuthoringReviewIntroValue
                        label="Introduction"
                        value={compare.intro?.content}
                        comparisonValue={comparisonTarget.intro?.content}
                        dataCy="side-by-side-intro-content"
                    />
                }
                right={
                    <AuthoringReviewIntroValue
                        label="Introduction"
                        value={comparisonTarget.intro?.content}
                        comparisonValue={compare.intro?.content}
                        showRemoved
                        dataCy="side-by-side-intro-content-comparison"
                    />
                }
            />
            <AuthoringSideBySideRow
                dataCy="side-by-side-intro-note-row"
                left={
                    <AuthoringReviewIntroValue
                        label="Notes"
                        value={compare.intro?.note}
                        comparisonValue={comparisonTarget.intro?.note}
                        dataCy="side-by-side-intro-note"
                    />
                }
                right={
                    <AuthoringReviewIntroValue
                        label="Notes"
                        value={comparisonTarget.intro?.note}
                        comparisonValue={compare.intro?.note}
                        showRemoved
                        dataCy="side-by-side-intro-note-comparison"
                    />
                }
            />
            <Repeat list={compare.tasks} pass="task" item={<AuthoringCompareTask />} />
            <Repeat list={missingTasks} pass="task" item={<AuthoringCompareMissingTask />} />
            {movements}
            <If value={guidanceItems.length}>
                <Typography sx={{ pl: 5, mt: 2 }} component="div" variant="subtitle1">
                    References
                </Typography>
            </If>
            {guidanceItems}
            <Box mb={60} />
        </Bound>
    )
}

function DetailsFieldValue({ label, value, dataCy }) {
    return (
        <Box py={1.5} data-cy={dataCy}>
            <Typography variant="overline">{label}</Typography>
            <Typography variant="h5">{value}</Typography>
        </Box>
    )
}
